import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, ChevronDown, ChevronRight, DoorClosed, DoorOpen, Headset, Lock, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { sidebarItems, SidebarItem } from "./items";
import LogoutModal from "./modal/logoutModal";
import { fleetrifyIcons } from "../../assets/icons/icons";
import { useSidenavControllers } from "../../controllers/useSidenavControllers";
import { useGetRelocationOrdersQuery } from "../../redux/services/relocationApi";
import { useGetAllOrdersQuery } from "../../redux/services/orders";
import { useSelector } from "react-redux";
import { selectAuth } from "../../redux/slices/authSlice";
import { usePermission } from "../../hooks/usePermission";
import { FLEETRIFYROUTEPATH } from "../../routes/routesPath";

const tourIdMap: Record<string, string> = {
  Dashboard: "dashboard-menu",
  //  Branch: "branch-menu",
  "Branch Management": "branch-menu",
    // Users: "users-menu",
  "User Management": "users-menu",
  Staff: "staff-menu",
  "Order Management": "order-management-menu",
  Orders: "orders-menu",
  "Relocation Requests": "relocation-menu",
  Message: "message-menu",
  // Services: "service-menu",
  "Service Management": "service-menu",
  Wallet: "wallet-menu",
  "AI Insights": "ai-insights-menu",
  Setting: "settings-menu",
  "Fleet Management": "fleet-management-menu",
};

const SideNav = () => {
  const location = useLocation();
  const { role, showLogoutModal, setShowLogoutModal } = useSidenavControllers();

  const auth = useSelector(selectAuth);
  const hasBranch = auth?.user?.has_branch === 1;
  const { permissionNames } = usePermission();
  const userPermissions: string[] = permissionNames;

  const isSuperAdmin =
    !!auth?.user?.is_head_of_org ||
    auth?.isAdmin ||
    auth?.user?.role?.name === "Super Admin";

  const hasModuleAccess = (item: SidebarItem): boolean => {
    if (!item.permissions || item.permissions.length === 0) return true;
    if (isSuperAdmin) return true;
    return item.permissions.some((p) => userPermissions.includes(p));
  };

  const planName = auth?.user?.company?.latestSubscription_plan?.name;

  const items = useMemo(
    () =>
      sidebarItems(role, hasBranch).filter(
        (item) => !item.planRequired || item.planRequired === planName
      ),
    [role, hasBranch, planName]
  );

  const { data: relocationData } = useGetRelocationOrdersQuery(
    { per_page: 1 },
    { pollingInterval: 30000 }
  );
  const pendingCount =
    (relocationData?.data?.counts?.pending ?? 0) +
    (relocationData?.data?.counts?.customer_countered ?? 0);

  const { data: dayToDayOrdersData } = useGetAllOrdersQuery(
    { order_type: "local_dispatch", status: "pending", per_page: 1 },
    { pollingInterval: 30000 }
  );
  const dayToDayPendingCount = dayToDayOrdersData?.data?.meta?.total ?? 0;

  // Each showBell nav item shows its own count — keyed by label since
  // "Orders" and "Relocation Requests" track different things.
  const bellCounts: Record<string, number> = {
    "Relocation Requests": pendingCount,
    Orders: dayToDayPendingCount,
  };

  const isPathActive = (path?: string) =>
    !!path &&
    (location.pathname === path || location.pathname.startsWith(`${path}/`));

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const activeParent = items.find((it) =>
      it.children?.some((c) => location.pathname === c.path)
    );
    if (activeParent) setOpenDropdown(activeParent.label);
  }, [location.pathname, items]);

  const toggleDropdown = (label: string) =>
    setOpenDropdown((prev) => (prev === label ? null : label));

  const handleMobileLinkClick = () => setIsMobileOpen(false);

  const LogoutDoorIcon = ({ size = 18 }: { size?: number }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <span
        className="inline-flex"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            <motion.span
              key="open"
              initial={{ rotateY: -90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: -90, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="inline-flex"
              style={{ transformOrigin: "left center" }}
            >
              <DoorOpen size={size} />
            </motion.span>
          ) : (
            <motion.span
              key="closed"
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: 90, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="inline-flex"
              style={{ transformOrigin: "left center" }}
            >
              <DoorClosed size={size} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    );
  };

  const ContactSupportCard = ({ onClick }: { onClick?: () => void }) => (
    <Link
      to={FLEETRIFYROUTEPATH.CONTACT_SUPPORT}
      onClick={onClick}
      className="block rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors p-4 text-left"
    >
      <Headset size={20} className="text-[#143887] mb-2" />
      <p className="text-xs text-[#00032A] leading-snug">
        Contact the Fleetrify support team for help, feedback, and more.
      </p>
      <span className="mt-3 inline-flex items-center justify-center w-full rounded-lg bg-fleetyellow text-[#00032A] text-sm font-semibold py-2">
        Contact Support
      </span>
    </Link>
  );

  const LockedOverlay = () => (
    <div className="absolute inset-0 flex items-center justify-center rounded-md z-10 pointer-events-none">
      <Lock size={14} className="text-gray-400" />
    </div>
  );

  const renderDesktopItem = (item: SidebarItem, idx: number) => {
    const { label, path, icon, children, showBell } = item;
    const isActive = isPathActive(path);
    const isDropdownOpen = openDropdown === label;
    const tourId = tourIdMap[label];
    const canAccess = hasModuleAccess(item);

    const iconEl = (active: boolean) =>
      typeof icon === "function"
        ? icon(active ? "#ffffff" : "#000", active ? "#ffffff" : "#0D160B")
        : icon;

    if (!canAccess) {
      return (
        <div key={idx} className="relative group" title="You don't have permission to access this module">
          <div className="relative flex items-center gap-3 p-3 rounded-md text-sm font-semibold text-gray-300 cursor-not-allowed select-none blur-[1px] opacity-60">
            <motion.figure whileHover={{ scale: 1 }}>
              {iconEl(false)}
            </motion.figure>
            {!isCollapsed && <span>{label}</span>}
          </div>
          <LockedOverlay />
        </div>
      );
    }

    return (
      <div key={idx} className="relative">
        {path ? (
          <Link
            id={tourId}
            to={path}
            className={`flex items-center gap-3 p-3 rounded-md text-sm font-semibold ${
              isActive
                ? "bg-[#143887] text-white"
                : "text-[#00032A] hover:bg-gray-100"
            }`}
          >
            <motion.figure
              whileHover={{ scale: 1.2, rotate: 8 }}
              whileTap={{ scale: 0.88 }}
              transition={{ type: "spring", stiffness: 450, damping: 12 }}
            >
              {iconEl(isActive)}
            </motion.figure>
            {!isCollapsed && (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="flex-1 truncate">{label}</span>
                {showBell && (bellCounts[label] ?? 0) > 0 && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <motion.span
                      animate={{ rotate: [-12, 12] }}
                      transition={{ duration: 1.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                      style={{ originX: "50%", originY: "0%" }}
                      className="inline-flex"
                    >
                      <Bell size={12} className="text-yellow-400" />
                    </motion.span>
                    <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center leading-none">
                      {bellCounts[label] > 99 ? "99+" : bellCounts[label]}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Link>
        ) : (
          <>
            <button
              id={tourId}
              onClick={() => toggleDropdown(label)}
              className={`flex items-center justify-between w-full p-3 rounded-md text-sm font-semibold ${
                isActive
                  ? "bg-[#143887] text-white"
                  : "text-[#00032A] hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center gap-3">
                <motion.figure
                  whileHover={{ scale: 1.2, rotate: 8 }}
                  whileTap={{ scale: 0.88 }}
                  transition={{ type: "spring", stiffness: 450, damping: 12 }}
                >
                  {typeof icon === "function"
                    ? icon(isDropdownOpen ? "#000" : "#00032A")
                    : icon}
                </motion.figure>
                {!isCollapsed && <span>{label}</span>}
              </div>
              {!isCollapsed &&
                (isDropdownOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                ))}
            </button>

            {!isCollapsed && isDropdownOpen && (
              <div className="ml-6 mt-2 flex flex-col gap-2">
                {children!.filter(hasModuleAccess).map((child, cIdx) => {
                  const childActive = location.pathname === child.path;
                  return (
                    <Link
                      key={cIdx}
                      id={tourIdMap[child.label]}
                      to={child.path || "#"}
                      className={`flex items-center gap-3 p-2 rounded-md text-sm ${
                        childActive
                          ? "bg-[#143887] text-white"
                          : "text-[#00032A] hover:bg-gray-100"
                      }`}
                    >
                      <motion.figure
                        whileHover={{ scale: 1.2, rotate: 8 }}
                        whileTap={{ scale: 0.88 }}
                        transition={{ type: "spring", stiffness: 450, damping: 12 }}
                      >
                        {typeof child.icon === "function"
                          ? child.icon(
                              childActive ? "#ffffff" : "#00032A",
                              childActive ? "#ffffff" : "#00032A"
                            )
                          : child.icon}
                      </motion.figure>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="flex-1 truncate">{child.label}</span>
                        {child.showBell && (bellCounts[child.label] ?? 0) > 0 && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <motion.span
                              animate={{ rotate: [-12, 12] }}
                              transition={{ duration: 1.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                              style={{ originX: "50%", originY: "0%" }}
                              className="inline-flex"
                            >
                              <Bell size={11} className="text-yellow-400" />
                            </motion.span>
                            <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center leading-none">
                              {bellCounts[child.label] > 99 ? "99+" : bellCounts[child.label]}
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderMobileItem = (item: SidebarItem, idx: number) => {
    const { label, path, icon, children } = item;
    const isActive = isPathActive(path);
    const isDropdownOpen = openDropdown === label;
    const canAccess = hasModuleAccess(item);

    if (!canAccess) {
      return (
        <div key={idx} className="relative group" title="You don't have permission to access this module">
          <div className="relative flex items-center gap-5 p-3 rounded-md text-sm font-semibold text-gray-300 cursor-not-allowed select-none blur-[1px] opacity-60">
            <figure>
              {typeof icon === "function" ? icon("", "#9CA3AF") : icon}
            </figure>
            <span>{label}</span>
          </div>
          <LockedOverlay />
        </div>
      );
    }

    return (
      <div key={idx} className="relative">
        {path ? (
          <Link
            to={path}
            onClick={handleMobileLinkClick}
            className={`flex items-center gap-5 p-3 rounded-md text-sm font-semibold ${
              isActive
                ? "bg-[#143887] text-white"
                : "text-[#00032A] hover:bg-gray-100"
            }`}
          >
            <figure>
              {typeof icon === "function"
                ? icon(
                    isActive ? "#0000" : "",
                    isActive ? "#ffffff" : "#0D160B"
                  )
                : icon}
            </figure>
            <span>{label}</span>
          </Link>
        ) : (
          <>
            <button
              onClick={() => toggleDropdown(label)}
              className={`flex items-center justify-between w-full p-3 rounded-md text-sm font-semibold ${
                isActive
                  ? "bg-[#143887] text-white"
                  : "text-[#00032A] hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center gap-5">
                <figure>
                  {typeof icon === "function"
                    ? icon(isDropdownOpen ? "#143887" : "#00032A")
                    : icon}
                </figure>
                <span>{label}</span>
              </div>
              {isDropdownOpen ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>

            {isDropdownOpen && (
              <div className="ml-6 mt-2 flex flex-col gap-2">
                {children!.filter(hasModuleAccess).map((child, cIdx) => {
                  const childActive = location.pathname === child.path;
                  return (
                    <Link
                      key={cIdx}
                      to={child.path || "#"}
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 p-2 rounded-md text-sm ${
                        childActive
                          ? "bg-[#143887] text-white"
                          : "text-[#00032A] hover:bg-gray-100"
                      }`}
                    >
                      <figure>
                        {typeof child.icon === "function"
                          ? child.icon(childActive ? "#143887" : "#00032A")
                          : child.icon}
                      </figure>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="flex-1 truncate">{child.label}</span>
                        {child.showBell && (bellCounts[child.label] ?? 0) > 0 && (
                          <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center leading-none">
                            {bellCounts[child.label] > 99 ? "99+" : bellCounts[child.label]}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* hamburger for mobile */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-md shadow-md"
        onClick={() => setIsMobileOpen((prev) => !prev)}
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white transition-all duration-300 h-screen relative ${
          isCollapsed ? "w-[90px]" : "w-[240px]"
        }`}
      >
        <style>
          {`
            .sidebar-scroll::-webkit-scrollbar {
              width: 1px;
            }
            .sidebar-scroll {
              scrollbar-width: thin;
            }
          `}
        </style>
        <button
          onClick={() => setIsCollapsed((prev) => !prev)}
          className={`absolute -right-3 top-6 z-50 p-2 bg-white shadow-md ${
            isCollapsed ? "border rounded-lg" : "border-0 rounded-none"
          }`}
        >
          {isCollapsed ? <X size={20} /> : <Menu size={20} />}
        </button>

        <Link to="/dashboard" className="flex gap-2 items-center mt-10 mb-5 p-4">
          {fleetrifyIcons.fleetrify_logo}
          {!isCollapsed && (
            <p className="font-extrabold text-[20px] text-[#143887]">Fleetrify</p>
          )}
        </Link>

        <div className="flex-1 overflow-y-auto px-2 sidebar-scroll">
          <div className="flex flex-col gap-2">
            {items.map((item, idx) => renderDesktopItem(item, idx))}
          </div>

          {!isCollapsed && (
            <div className="mt-4 mb-2">
              <ContactSupportCard />
            </div>
          )}
        </div>

        <button
          id="logout-btn"
          onClick={() => setShowLogoutModal(true)}
          className="flex gap-3 items-center mt-auto p-3 border-t w-full group"
        >
          <figure className="text-[#00032A] group-hover:text-red-500 transition-colors">
            <LogoutDoorIcon size={18} />
          </figure>
          {!isCollapsed && <span>Log out</span>}
        </button>
      </aside>

      {/* mobile sidebar */}
      <div
        className={`lg:hidden fixed inset-0 bg-white z-40 transform transition-transform duration-300 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col justify-between h-full p-5 pt-14 overflow-y-auto sidebar-scroll">
          <Link
            to="/dashboard"
            className="flex gap-2 items-center mb-10"
            onClick={handleMobileLinkClick}
          >
            {fleetrifyIcons.fleetrify_logo}
            <p className="font-extrabold text-[20px] text-[#143887] mt-10">
              Fleetrify
            </p>
          </Link>

          <div className="flex flex-col gap-2">
            {items.map((item, idx) => renderMobileItem(item, idx))}
          </div>

          <div className="mt-4 mb-2">
            <ContactSupportCard onClick={handleMobileLinkClick} />
          </div>

          <button
            onClick={() => {
              setShowLogoutModal(true);
              setIsMobileOpen(false);
            }}
                      //  className="flex gap-3 items-center mt-auto p-3 border-t w-full"
            className="flex gap-3 items-center mt-auto p-3 border-t w-full group"
          >
             {/* <figure>{fleetrifyIcons.logout}</figure> */}
            <figure className="text-[#00032A] group-hover:text-red-500 transition-colors">
              <LogoutDoorIcon size={18} />
            </figure>
            <span>Log out</span>
          </button>
        </div>
      </div>

      <LogoutModal
        isModalOpen={showLogoutModal}
        setIsModalOpen={setShowLogoutModal}
      />
    </>
  );
};

export default SideNav;
